import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Database,
  Sparkles,
  CheckCircle2,
  RefreshCw,
  Layers,
  ArrowUpRight,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  APPENDING_SERVICES,
  DATA_ADDITION_CYCLE,
  EMAIL_AGGREGATION_ADVANTAGES,
  HIGHLIGHT_PILLARS,
} from "../../Data/dataAppendingData";

import "../../Style/services/data-appending.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function DataAppending() {
  const [activeCycleIndex, setActiveCycleIndex] = useState(0);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeCycle = DATA_ADDITION_CYCLE[activeCycleIndex];
  const ActiveCycleIcon = activeCycle.icon;

  return (
    <div className="appending-page">
      <Seo
        title="B2B Data Appending & Enrichment Services | Conectify Tech"
        description="Enrich your existing business database with verified contact details, direct dials, social handles, and 5-step authentication."
      />

      {/* Hero Section */}
      <section className="appending-hero">
        <Container className="appending-hero__container">
          <motion.div
            className="appending-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Database Enrichment & Hygiene</span>
            </div>

            <h1 className="appending-hero__title">
              Enrich your Database with{" "}
              <span className="text-gradient">Relevant Contact Information</span>
            </h1>

            <p className="appending-hero__lead">
              Adding data is a process that allows you to keep up with your contact list without
              compromising on quality. Enrich your database to generate qualified leads and reach your
              sales goal with ease.
            </p>

            <p className="appending-hero__sublead">
              Are you missing contact links in your existing database? The solution lies in the use
              of Conectify Tech’s data aggregation services. With the correct contact details, we
              guarantee qualified leads and fast conversions. From email IDs to phone numbers and
              company locations, Conectify Tech keeps you up to date with the latest details.
            </p>

            <div className="appending-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Enrich My Existing Database
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Request Sample Audit
              </Button>
            </div>
          </motion.div>

          {/* Interactive Before & After Data Enrichment Card */}
          <motion.div
            className="appending-hero__preview"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="appending-preview-card">
              <div className="appending-preview-card__header">
                <Database size={18} className="text-primary" />
                <span>Live Record Enrichment Example Comparison</span>
              </div>

              {/* Before State */}
              <div className="enrich-record enrich-record--before">
                <div className="enrich-record__tag">Incomplete File (Before)</div>
                <div className="enrich-record__row">
                  <span className="label">Name:</span>
                  <span className="value">Michael Chang</span>
                </div>
                <div className="enrich-record__row">
                  <span className="label">Company:</span>
                  <span className="value">Acrobat Global</span>
                </div>
                <div className="enrich-record__row enrich-record__row--missing">
                  <span className="label">Email:</span>
                  <span className="value text-danger">Missing / Bounce</span>
                </div>
                <div className="enrich-record__row enrich-record__row--missing">
                  <span className="label">Phone:</span>
                  <span className="value text-danger">Missing Direct Line</span>
                </div>
              </div>

              {/* Transformation Divider */}
              <div className="enrich-divider">
                <RefreshCw size={16} className="enrich-divider__icon" />
                <span>Conectify Tech Appending Engine</span>
              </div>

              {/* After State */}
              <div className="enrich-record enrich-record--after">
                <div className="enrich-record__tag enrich-record__tag--success">
                  Enriched Record (After)
                </div>
                <div className="enrich-record__row">
                  <span className="label">Email:</span>
                  <span className="value text-success font-semibold">
                    m.chang@example.com
                  </span>
                </div>
                <div className="enrich-record__row">
                  <span className="label">Phone:</span>
                  <span className="value text-success font-semibold">+1 000 000 001 (Direct)</span>
                </div>
                <div className="enrich-record__row">
                  <span className="label">Profile:</span>
                  <span className="value">VP Operations (Verified)</span>
                </div>
              </div>

              {/* Highlight Pillars */}
              <div className="appending-preview-card__footer">
                {HIGHLIGHT_PILLARS.map((pillar) => (
                  <span key={pillar} className="appending-pill">
                    <CheckCircle2 size={12} className="text-primary" />
                    {pillar}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* 4 Appending Modalities */}
      <section className="appending-services">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Full-Spectrum Enrichment</span>
            <h2 className="section-title">Comprehensive Appending Capabilities</h2>
            <p className="section-desc">
              Customized enrichment modalities targeted at filling missing links across every corporate contact point.
            </p>
          </div>

          <div className="appending-services__grid">
            {APPENDING_SERVICES.map((srv) => {
              const Icon = srv.icon;
              return (
                <motion.div
                  key={srv.title}
                  className="appending-service-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="appending-service-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{srv.title}</h4>
                  <p>{srv.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Data Addition Cycle (Interactive Stepper) */}
      <section className="appending-cycle">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Quality Protocol</span>
            <h2 className="section-title">Data Addition Cycle</h2>
            <p className="section-desc">
              A 5-phase systematic methodology ensuring pristine accuracy, data hygiene, and legal authentication.
            </p>
          </div>

          {/* Stepper Navigation */}
          <div className="appending-cycle__nav">
            {DATA_ADDITION_CYCLE.map((cycle, idx) => (
              <button
                key={cycle.id}
                type="button"
                className={`appending-cycle-btn ${activeCycleIndex === idx ? "is-active" : ""}`}
                onClick={() => setActiveCycleIndex(idx)}
              >
                <span className="num">{cycle.step}</span>
                <span className="title">{cycle.title}</span>
              </button>
            ))}
          </div>

          {/* Active Cycle Detail Showcase */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCycle.id}
              className="appending-cycle-detail"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <div className="appending-cycle-detail__icon-wrap">
                <ActiveCycleIcon size={34} />
                <span className="cycle-badge">Phase {activeCycle.step}</span>
              </div>
              <div className="appending-cycle-detail__text">
                <h3>{activeCycle.title}</h3>
                <p>{activeCycle.desc}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </Container>
      </section>

      {/* Single Destination for B2B Email Aggregation */}
      <section className="appending-aggregation">
        <Container>
          <div className="appending-aggregation__box">
            <div className="appending-aggregation__content">
              <span className="section-eyebrow">Email Intelligence</span>
              <h2>A Single Destination for B2B Email Aggregation Services</h2>
              <p>
                To help companies grow their sales to their full potential, Conectify Tech prioritizes
                customer reach by providing complimentary email services worldwide. Double your influence
                with effective marketing automation strategies and drive your sales into the funnel for
                long-term digitized asset development. Get access to location-based segmented records for
                consistent market opportunities.
              </p>
            </div>

            <div className="appending-aggregation__cta">
              <Layers size={36} className="text-primary mb-3" />
              <h4>Accelerated Sales Promotion</h4>
              <p>Location-based segmented records for global market development.</p>
              <Button icon={<ArrowUpRight size={16} />} onClick={handleConsultation}>
                Connect With Data Team
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Advantages of Email Aggregation Services */}
      <section className="appending-advantages">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Measurable Outcomes</span>
            <h2 className="section-title">Advantages of Email Aggregation Services</h2>
            <p className="section-desc">
              Scale your growth with smart, result-driven B2B email attachment solutions. Empower your
              decision-makers by providing accelerated sales promotions through our service.
            </p>
          </div>

          <div className="appending-advantages__grid">
            {EMAIL_AGGREGATION_ADVANTAGES.map((adv) => {
              const Icon = adv.icon;
              return (
                <motion.div
                  key={adv.title}
                  className="appending-advantage-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="appending-advantage-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{adv.title}</h4>
                  <p>{adv.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="appending-cta">
        <Container>
          <div className="appending-cta__box">
            <h2>Ready to Enrich and Authenticate Your Database?</h2>
            <p>
              Eliminate missing records, remove dormant inboxes, and unlock revenue opportunities from
              your existing pipeline with Conectify Tech.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Start Appending Your Data
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}